import React from 'react';

import { Button, ModalCard } from '~components';
import Icon, { faFileCsv } from '~components/icon';
import { TableView } from '~components/table-view';

import columns, { columnsWithRetryable } from '~support-at-home/forms/budgets/bulkCopyFailureList';

import { BudgetFailureRow } from '~support-at-home/actions/budgets';

import { exportBulkCopyFailuresCsv } from './bulkCopyCsv';

interface ComponentProps {
  failures: BudgetFailureRow[];
  /**
   * Heading suffix, rendered as `{n} Budget(s) {headingSuffix}` — the tense differs before the copy
   * ("Will Not Be Copied") and after it ("Not Copied").
   */
  headingSuffix: string;
  /** Distinguishes the preview and review instances: data-cy hooks, the table id, and the CSV filename. */
  idPrefix: string;
  /**
   * Show the "Can Retry" column and carry it into the CSV. Step 4 only — see columnsWithRetryable for why
   * step 3 leaves it out.
   */
  showRetryable?: boolean;
}

/**
 * The "not copied" table shared by the preview (step 3) and review (step 4) steps of the bulk-copy wizard
 * (TAV-2649a). Budgets land here when the API could not copy them at all — most commonly because the
 * client's package no longer resolves to a funding source — as opposed to the needs_review rows, which DID
 * copy. One row per budget with the API's reason, so a coordinator can fix the data and retry just these.
 *
 * Renders nothing when there are no failures, so both callers can drop it in unconditionally.
 *
 * At step 4 the list covers BOTH not-copied buckets the API reports — budgets whose copy failed and can be
 * retried, and ids that never resolved to a budget at all — kept in one table with a "Can Retry" column
 * rather than split in two, because the coordinator's question is "what didn't copy", not "why not".
 */
const FailureList: React.FC<ComponentProps> = ({ failures, headingSuffix, idPrefix, showRetryable = false }) => {
  if (failures.length === 0) return null;

  // `retryable` is absent on the preview's failures (nothing attempted yet), so undefined reads as retryable
  // — a row is only called out as terminal when the API has explicitly said so.
  const isRetryable = (failure: BudgetFailureRow): boolean => failure.retryable !== false;

  const rows = failures.map((failure) => ({
    ...failure,
    id: failure.budget_id,
    retry: isRetryable(failure) ? 'Yes' : 'No',
  }));
  const noun = failures.length === 1 ? 'Budget' : 'Budgets';

  return (
    <ModalCard header={`${failures.length} ${noun} ${headingSuffix}`} className="w-100 mt-3">
      {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss */}
      <div className="bulk-copy-scroll-table">
        <TableView
          id={`${idPrefix}-failure-table`}
          data={rows}
          columns={showRetryable ? columnsWithRetryable : columns}
          readonly
          striped
        />
      </div>
      <div className="d-flex justify-content-end mt-3">
        <Button
          color="info"
          onClick={() =>
            exportBulkCopyFailuresCsv(
              failures.map((failure) => ({
                client_name: failure.client_name,
                budget_id: failure.budget_id,
                budget_reference: failure.ref,
                reason: failure.reason,
                retryable: isRetryable(failure),
              })),
              // Per-step filename so a preview download and a post-copy download don't overwrite each
              // other: bulk-copy-preview-not-copied-<date>.csv / bulk-copy-review-not-copied-<date>.csv.
              `${idPrefix}-not-copied`,
            )
          }
          dataCy={`${idPrefix}-failure-export-button`}
        >
          <Icon icon={faFileCsv} className="me-2" />
          Download not-copied list
        </Button>
      </div>
    </ModalCard>
  );
};

export default FailureList;
